import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import useInfiniteService from '@/data/hook/useInfiniteService';
import {
  createCourseModule,
  CreateCourseModule,
  deleteCourseModule,
  reorderCourseModules,
} from '@/data/services/courseModuleServices';
import { courseModulesQueryKeys } from '@/data/services/querykeys';
import alert from '@/utils/shared/alert';
import { mountKey } from '@/utils/courses';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { reorder } from '@/utils/list';

type UseCourseModulesProps = {
  courseLineId: number;
};

const useCourseModules = ({ courseLineId }: UseCourseModulesProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.modulesTab',
  });
  const { tree } = useTreeCourses();

  const {
    results: modules,
    isInitialLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    cacheManager,
  } = useInfiniteService({
    ...courseModulesQueryKeys.list(courseLineId)._ctx.infinity,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateCourseModule) =>
      createCourseModule(courseLineId, data),
    onSuccess: module => {
      cacheManager.add(module, { position: 'end' });
      const parentKey = mountKey({ type: 'courseLine', id: courseLineId });
      tree?.current.upsert(
        {
          id: mountKey({ type: 'module', id: module.id }),
          name: module.name,
          type: 'module',
          realId: module.id,
        },
        parentKey,
      );
      alert.success(t('createSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (moduleId: number) =>
      deleteCourseModule(courseLineId, moduleId),
    onSuccess: (_, moduleId) => {
      cacheManager.remove(moduleId);
      tree?.current.delete(mountKey({ type: 'module', id: moduleId }));
      alert.success(t('deleteSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const reorderMutation = useMutation({
    onMutate: moduleIds => {
      cacheManager.set(reorder(modules, moduleIds));
    },
    mutationFn: (moduleIds: number[]) =>
      reorderCourseModules(
        courseLineId,
        moduleIds.map(id => ({ id })),
      ),
    onSuccess: () => {
      alert.success(t('reorderSuccess'));
    },
    onError: err => {
      cacheManager.undo();
      alert.error(getErrorMessage(err));
    },
  });

  return {
    modules,
    isLoading: isInitialLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    cacheManager,
    createModule: createMutation.mutate,
    isCreating: createMutation.isPending,
    deleteModule: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    reorderModules: reorderMutation.mutate,
    isReordering: reorderMutation.isPending,
  };
};

export default useCourseModules;
